"use client";

import React, { useState } from "react";
import { useSelector } from "react-redux";
import { ChevronDownIcon, ChevronUpIcon } from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import selectedColorBrand from "@/config/tailwind-colors/brand-colors";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { RootState } from "@/store/root-reducer";
import {
  IdentificationType,
  PolicyRenewalContentProps,
} from "@/types/confirmation-types";
import {
  rearrangeDateFormat,
  formatTelephoneNumber,
  getIdentificationLabel,
} from "@/utils/confirmation.utils";
import { travelFromCountryData } from "@/utils/question-location-from.utils";

function PolicyRenewalContent({
  yourPolicyDetailTitle,
  yourPolicyNameText,
  orderTextDate,
  typeOfCoverText,
  travelFromText,
  coverDatesText,
  policyCostText,
  policyNumberText,
  passportIdText,
  emailAdressText,
  phoneNumberText,
}: Readonly<PolicyRenewalContentProps>) {
  const [policyRenewalAccordionOpen, setPolicyRenewalAccordionOpen] =
    useState(true);

  const { referenceId, attributes } = useSelector(
    (state: RootState) => state.quote
  );
  const { current } = useWizard();
  const { t } = useWizardTranslation();

  const fromLocation = travelFromCountryData.find(
    (country) => country.code === attributes?.tripDetails?.fromLocation
  )?.displayName;

  function togglePolicyRenewalAccordion() {
    setPolicyRenewalAccordionOpen(!policyRenewalAccordionOpen);
  }

  if (!referenceId)
    return <div data-testid="notFound-testid">Policy Details Not Found</div>;

  const telephoneNumber = formatTelephoneNumber(
    attributes?.organiser?.telephone
  );

  const identificationType = attributes?.organiser
    ?.identificationType as IdentificationType;

  return (
    <div
      className="flex flex-col gap-spacing-s md:gap-spacing-m lg:gap-spacing-l"
      data-testid="policyRenewalContent-testid"
    >
      <button
        id="policy-renewal-accordion-button"
        type="button"
        className="transition duration-300 w-full"
        onClick={togglePolicyRenewalAccordion}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            togglePolicyRenewalAccordion();
          }
        }}
      >
        <h1 className=" flex flex-row text-left justify-between font-header-font text-[23px] md:text-[31px] leading-[27.6px] md:leading-[34.1px]">
          {yourPolicyDetailTitle}
          {policyRenewalAccordionOpen ? (
            <ChevronUpIcon stroke={selectedColorBrand.dark["text-title"]} />
          ) : (
            <ChevronDownIcon stroke={selectedColorBrand.dark["text-title"]} />
          )}
        </h1>
      </button>
      <div
        data-testid="accordion-content-testid"
        className={`space-y-spacing-s md:space-y-spacing-m overflow-hidden transition-opacity duration-500 
    ${policyRenewalAccordionOpen ? "opacity-100" : "opacity-0 hidden"}
    
    `}
      >
        <div>
          <p className="text-2xs font-normal leading-6 text-dark-text-functional-subtle">
            {yourPolicyNameText}
          </p>
          <p className="text-dark-text-title text-[20px] md:text-[25px] font-bold leading-[23px]">
            {` ${attributes?.organiser?.firstName} ${attributes?.organiser?.lastName}`}
          </p>
        </div>
        <hr className="border border-dark-border-strong-2" />
        <div>
          <p className="text-2xs font-normal leading-6 text-dark-text-functional-subtle">
            {orderTextDate}
          </p>
          <p
            className="text-dark-text-title text-[20px] md:text-[25px] font-bold leading-[23px]"
            data-testid="date-testid"
          >
            {rearrangeDateFormat(attributes?.tripDetails?.policyCreationDate)}
          </p>
        </div>
        <hr className="border border-dark-border-strong-2" />
        <div>
          <p className="text-2xs font-normal leading-6 text-dark-text-functional-subtle">
            {typeOfCoverText}
          </p>
          <p className="text-dark-text-title text-[20px] md:text-[25px] font-bold leading-[23px]">
            {attributes?.tripDetails?.policyType === "SINGLE_TRIP"
              ? t("single_trip", "Single Trip")
              : t("annual_multi_trip", "Annual Multi Trip")}
          </p>
        </div>
        {current.uiElementControls?.showTravelFromField && (
          <>
            <hr className="border border-dark-border-strong-2" />
            <div>
              <p className="text-2xs font-normal leading-6 text-dark-text-functional-subtle">
                {travelFromText}
              </p>
              <p className="text-dark-text-title text-[20px] md:text-[25px] font-bold leading-[23px]">
                {fromLocation}
              </p>
            </div>
          </>
        )}
        <hr className="border border-dark-border-strong-2" />
        <div>
          <p className="text-2xs font-normal leading-6 text-dark-text-functional-subtle">
            {coverDatesText}
          </p>
          <p className="text-dark-text-title text-[20px] md:text-[25px] font-bold leading-[23px]">
            {rearrangeDateFormat(attributes?.tripDetails?.startDate)} –{" "}
            {rearrangeDateFormat(attributes?.tripDetails?.policyEndDate)}
          </p>
        </div>
        <hr className="border border-dark-border-strong-2" />
        <div>
          <p className="text-2xs font-normal leading-6 text-dark-text-functional-subtle">
            {policyCostText}
          </p>
          <p className="text-dark-text-title text-[20px] md:text-[25px] font-bold leading-[23px]">
            {`${(current.brandSpecificParams?.currencySymbol as string) ?? ""}`}
            {
              attributes?.paymentDetails?.paymentOptions[0]?.cardPayment?.card
                ?.price
            }
          </p>
        </div>
        <hr className="border border-dark-border-strong-2" />
        <div>
          <p className="text-2xs font-normal leading-6 text-dark-text-functional-subtle">
            {policyNumberText}
          </p>
          <p className="text-dark-text-title text-[20px] md:text-[25px] font-bold leading-[23px]">
            {attributes?.policyNumber
              ? attributes?.policyNumber
              : t("policy_not_Created_yet", "Policy Not Created Yet")}
          </p>
        </div>
        {current.uiElementControls?.showPassportField && (
          <>
            <hr className="border border-dark-border-strong-2" />
            <div>
              <p className="text-2xs font-normal leading-6 text-dark-text-functional-subtle">
                {passportIdText}
              </p>
              <p className="text-dark-text-title text-[20px] md:text-[25px] font-bold leading-[23px]">
                {attributes?.paymentDetails?.passportId}
              </p>
            </div>
          </>
        )}
        {current.uiElementControls?.showIdentificationType &&
          identificationType && (
            <>
              <hr className="border border-dark-border-strong-2" />
              <div>
                <p className="text-2xs font-normal leading-6 text-dark-text-functional-subtle">
                  {t(
                    getIdentificationLabel(identificationType),
                    identificationType
                  )}
                </p>
                <p className="text-dark-text-title text-[20px] md:text-[25px] font-bold leading-[23px]">
                  {attributes?.organiser?.identificationNumber}
                </p>
              </div>
            </>
          )}
        <hr className="border border-dark-border-strong-2" />
        <div>
          <p className="text-2xs font-normal leading-6 text-dark-text-functional-subtle">
            {emailAdressText}
          </p>
          <p
            className="text-dark-text-title text-[20px] md:text-[25px] font-bold leading-[23px]"
            style={{ wordBreak: "break-word" }}
          >
            {attributes?.organiser?.email}
          </p>
        </div>
        <hr className="border border-dark-border-strong-2" />
        <div>
          <p className="text-2xs font-normal leading-6 text-dark-text-functional-subtle">
            {phoneNumberText}
          </p>
          <p className="text-dark-text-title text-[20px] md:text-[25px] font-bold leading-[23px]">
            {telephoneNumber}
          </p>
        </div>
      </div>
    </div>
  );
}

export default PolicyRenewalContent;
